import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Workflow, WorkflowFilters } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const WorkflowsList: React.FC = () => {
  const { user } = useAuth();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<WorkflowFilters>({
    category: 'all',
    tags: [],
    search: '',
    favorites: false
  });

  useEffect(() => {
    fetchCategories();
    fetchTags();
  }, []);

  useEffect(() => {
    fetchWorkflows();
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/workflows/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/workflows/tags`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAvailableTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchWorkflows = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.category !== 'all') params.append('category', filters.category);
      if (filters.tags.length > 0) filters.tags.forEach(tag => params.append('tags', tag));
      if (filters.search) params.append('search', filters.search);
      if (filters.favorites) params.append('favorites', 'true');

      const response = await axios.get(`${API_BASE}/workflows?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWorkflows(response.data);
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (workflowId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card navigation
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE}/workflows/${workflowId}/favorite`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setWorkflows(workflows.map(w => 
        w.id === workflowId ? { ...w, is_favorite: response.data.is_favorite } : w
      ));
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const updateFilters = (newFilters: Partial<WorkflowFilters>) => {
    setFilters({ ...filters, ...newFilters });
  };

  const toggleTag = (tag: string) => {
    const newTags = filters.tags.includes(tag)
      ? filters.tags.filter(t => t !== tag)
      : [...filters.tags, tag];
    updateFilters({ tags: newTags });
  };

  const clearFilters = () => {
    setFilters({
      category: 'all',
      tags: [],
      search: '',
      favorites: false
    });
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Image Generation': return '🎨';
      case 'Text Processing': return '📝';
      case 'Video Generation': return '🎬';
      case 'Audio Processing': return '🎵';
      default: return '⚙️';
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-gray-600">Loading workflows...</div>;
  }

  const activeFiltersCount = (filters.category !== 'all' ? 1 : 0) + 
                           filters.tags.length + 
                           (filters.search ? 1 : 0) + 
                           (filters.favorites ? 1 : 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900">Your Workflows</h2>
        {activeFiltersCount > 0 && (
          <button
            onClick={clearFilters}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors text-sm"
          >
            Clear Filters ({activeFiltersCount})
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search workflows..."
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          value={filters.search}
          onChange={(e) => updateFilters({ search: e.target.value })}
        />
      </div>

      {/* Category Filters */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => updateFilters({ category: 'all' })}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filters.category === 'all'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            All Categories
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => updateFilters({ category })}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                filters.category === category
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              <span>{getCategoryIcon(category)}</span>
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Quick Filters */}
      <div className="mb-6 flex flex-wrap gap-3">
        <button
          onClick={() => updateFilters({ favorites: !filters.favorites })}
          className={`px-3 py-1 rounded-full text-sm transition-colors ${
            filters.favorites
              ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
          }`}
        >
          ⭐ Favorites Only
        </button>
        
        {availableTags.slice(0, 8).map((tag) => (
          <button
            key={tag}
            onClick={() => toggleTag(tag)}
            className={`px-3 py-1 rounded-full text-sm transition-colors ${
              filters.tags.includes(tag)
                ? 'bg-blue-100 text-blue-800 border border-blue-300'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
            }`}
          >
            #{tag}
          </button>
        ))}
      </div>

      {/* Workflows Grid */}
      {workflows.length === 0 ? (
        <div className="text-center text-gray-500 py-12">
          {activeFiltersCount > 0 ? 'No workflows match your filters.' : 'No workflows available.'}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {workflows.map((workflow) => (
            <div
              key={workflow.id}
              className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-1 border-l-4 border-blue-500 relative group"
              onClick={() => {
                const route = workflow.workflow_type === 'slug'
                  ? `/workflow-slug/${workflow.id}`
                  : `/workflow/${workflow.id}`;
                window.location.href = route;
              }}
            >
              {/* Favorite Button */}
              <button
                onClick={(e) => toggleFavorite(workflow.id, e)}
                className={`absolute top-4 right-4 p-1 rounded-full transition-all opacity-0 group-hover:opacity-100 text-xl ${
                  workflow.is_favorite
                    ? 'text-yellow-500 opacity-100'
                    : 'text-gray-400 hover:text-yellow-500'
                }`}
              >
                {workflow.is_favorite ? '⭐' : '☆'}
              </button>

              {/* Category Badge */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">{getCategoryIcon(workflow.category)}</span>
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                  {workflow.category}
                </span>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-2 pr-8">{workflow.name}</h3>
              <p className="text-gray-600 text-sm mb-3">
                {workflow.workflow_type === 'slug'
                  ? (workflow.override_count || 0)
                  : Object.keys(workflow.configurable_params || {}).length} configurable parameters
              </p>

              {/* Tags */}
              {workflow.tags && workflow.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {workflow.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs"
                    >
                      #{tag}
                    </span>
                  ))}
                  {workflow.tags.length > 3 && (
                    <span className="text-gray-500 text-xs">
                      +{workflow.tags.length - 3} more
                    </span>
                  )}
                </div>
              )}

              {/* Last Updated */}
              <div className="text-xs text-gray-500 mt-2">
                Updated {new Date(workflow.updated_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkflowsList;
